import { ChangeEventHandler, useCallback, useEffect, useState } from 'react';
import { startOfDay, startOfMonth, subMonths } from 'date-fns';
import { DialogActions, valString } from '@bymason-private/legacy-pattern-components';
import { useAuth } from '@bymason-private/auth-provider';
import {
  Button,
  ControllerModal,
  DateRange,
  DateRangePicker,
  DialogContent,
  Stack,
  ControllerTextField,
} from '@bymason-private/legacy-pattern-components';
import { useGenerateUsageReport } from '@bymason/controller-api-hooks';

interface GenerateConnectivityReportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (email: string, startDate: Date, endDate: Date) => void;
  onError?: (message: string, code?: number) => void;
}

const emailSchema = valString().email('Invalid email').required('Required');
const initDateRange: DateRange<Date> = [subMonths(startOfMonth(startOfDay(new Date())), 1), new Date()];

export function GenerateConnectivityReportModal({
  onSuccess,
  onError,
  open,
  onClose,
}: GenerateConnectivityReportModalProps) {
  const { authToken = '', userInfo } = useAuth();
  const [generateReport, , reportError, reportBusy, reportErrorCode] = useGenerateUsageReport(authToken);

  const [dateRange, setDateRange] = useState<DateRange<Date>>(initDateRange);
  const [email, setEmail] = useState(userInfo?.email);
  const [emailError, setEmailError] = useState('');
  const [rangeIsValid, setRangeIsValid] = useState(true);
  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);
  const [reportCallWasMade, setReportCallWasMade] = useState(false);

  const handleRangeChange = useCallback((newDateRange: DateRange<Date>) => {
    setDateRange(newDateRange);
  }, []);

  const handleRangeValidate = useCallback((isValid: boolean) => {
    setRangeIsValid(isValid);
  }, []);

  const handleEmailChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = useCallback(
    async (evt) => {
      setEmail(evt.target.value);

      try {
        await emailSchema.validate(evt.target.value);
      } catch (error) {
        setEmailError(String(typeof error === 'object' ? (error as Error).message : error));
      }
    },
    []
  );

  const handleClose = useCallback(() => {
    onClose && onClose();
    // reset state to init
    setDateRange(initDateRange);
    setEmailError('');
    setRangeIsValid(true);
    setReportCallWasMade(false);
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    if (email && dateRange[0] && dateRange[1] && rangeIsValid) {
      generateReport({ email, startDate: dateRange[0], endDate: dateRange[1] });
    }
  }, [dateRange, email, rangeIsValid, generateReport]);

  useEffect(() => {
    if (reportBusy && !reportCallWasMade) {
      setReportCallWasMade(true);
    } else if (!reportBusy && !reportError && reportCallWasMade) {
      if (email && dateRange[0] && dateRange[1] && onSuccess) {
        onSuccess(email, dateRange[0], dateRange[1]);
      }
      setReportCallWasMade(false);
      handleClose();
    } else if (!reportBusy && reportError && reportCallWasMade) {
      let errorMessage = reportError;
      errorMessage =
        reportErrorCode === 401
          ? `You do not have permission to generate a connectivity report at this time. Please contact your administrator to request access.`
          : 'A connectivity report cannot be generated at this time. Please try again later.';
      console.error('report error:', errorMessage);

      setReportCallWasMade(false);
      onError && onError(errorMessage, reportErrorCode);
    }
  }, [
    dateRange,
    email,
    handleClose,
    onError,
    onSuccess,
    reportBusy,
    reportCallWasMade,
    reportError,
    reportErrorCode,
  ]);

  return (
    <ControllerModal
      onClose={handleClose}
      disableEscapeKeyDown
      open={open}
      subTitle={`We will email you a device usage CSV report when it's ready.`}
      title='Generate device usage report'
      data-modal-name='connectivity-report'
    >
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          <ControllerTextField
            onChange={handleEmailChange}
            error={!!emailError}
            errorText={emailError}
            label='Email'
            required
            value={email}
          />
          <DateRangePicker
            // allowSameDateSelection
            disableFuture
            endDateLabel='End date'
            endDateHelperText='End range of report'
            onChange={handleRangeChange}
            onClose={() => {
              setOpenDateRangePicker(false);
            }}
            onOpen={() => {
              setOpenDateRangePicker(true);
            }}
            onValidate={handleRangeValidate}
            open={openDateRangePicker}
            required
            startDateHelperText='Start range of report'
            startDateLabel='Start date'
            value={dateRange}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={handleClose} variant='text'>
          Cancel
        </Button>
        <Button
          color='primary'
          disabled={Boolean(emailError || !rangeIsValid)}
          onClick={handleSubmit}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}
